import { Link, useNavigate, useParams } from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AppHeader } from "../components/AppHeader";
import { Avatar } from "../components/Avatar";
import { MediaTile } from "../components/MediaTile";
import { PostMediaStories } from "../components/MediaStory";
import { PostReactions } from "../components/PostReactions";
import { PostComments } from "../components/PostComments";
import { apiFetch } from "../lib/api";
import { formatDate } from "../lib/dates";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { can, formatEventDate as formatPicturedDate } from "@family/shared";
import type { Post } from "@family/shared";

export function PostPage() {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const meQuery = useMe();

  const postQuery = useQuery<Post>({
    queryKey: ["post", postId],
    queryFn: () => apiFetch<Post>(`/posts/${postId}`),
    enabled: Boolean(postId),
  });

  const membershipPrimary = resolveMembership(meQuery.data);
  const slug = membershipPrimary?.familySlug;
  const role = membershipPrimary?.role;
  const currentUserId = meQuery.data?.user.id;
  const isOwn = postQuery.data?.author.id === currentUserId;
  const canDelete =
    role &&
    postQuery.data &&
    (isOwn ? can(role, "post:delete:own") : can(role, "post:delete:any"));

  const deletePost = useMutation<void, Error, void>({
    mutationFn: () =>
      apiFetch(`/families/${slug}/posts/${postId}`, { method: "DELETE" }),
    onSuccess: () => {
      if (slug) {
        void queryClient.invalidateQueries({ queryKey: ["posts", slug] });
      }
      navigate("/", { replace: true });
    },
  });

  function handleDeletePost() {
    if (!postQuery.data) return;
    const label =
      postQuery.data.title || postQuery.data.body.slice(0, 40) + "…";
    if (
      window.confirm(`Delete post "${label}"? This cannot be undone.`)
    ) {
      deletePost.mutate();
    }
  }

  const membership = resolveMembership(meQuery.data);
  const watermarkVars = {
    user:
      meQuery.data?.user.displayName ??
      meQuery.data?.user.email ??
      meQuery.data?.user.phone ??
      "",
    family: membership?.familyName ?? "",
    phone: meQuery.data?.user.phone ?? null,
    email: meQuery.data?.user.email ?? null,
  };

  return (
    <>
      <AppHeader />
      <main className="page stack-lg">
        <Link to="/" className="text-muted" style={{ textDecoration: "none" }}>
          ← Back to timeline
        </Link>

        {postQuery.isLoading && <p className="text-muted">Loading…</p>}
        {postQuery.error && (
          <div className="stack">
            <p className="text-error">
              {(postQuery.error as Error).message.includes("403")
                ? "You don't have access to this post."
                : (postQuery.error as Error).message.includes("404")
                  ? "Post not found."
                  : (postQuery.error as Error).message}
            </p>
          </div>
        )}

        {postQuery.data && (
          <article className="card stack">
            {/* Posted-by header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--s-2)",
                fontSize: "0.95rem",
                flexWrap: "wrap",
              }}
            >
              <Avatar
                src={postQuery.data.author.avatarUrl ?? null}
                name={postQuery.data.author.displayName}
                size={40}
              />
              <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.25 }}>
                <span style={{ fontWeight: 600 }}>
                  {postQuery.data.author.displayName ?? "Unknown"}
                </span>
                <span className="text-subtle" style={{ fontSize: "0.85rem" }}>
                  Posted {formatDate(postQuery.data.createdAt)}
                </span>
              </div>
            </div>

            <div className="row-between">
              {postQuery.data.title ? (
                <h1 style={{ margin: 0 }}>{postQuery.data.title}</h1>
              ) : (
                <span />
              )}
              {canDelete && (
                <button
                  className="btn-tertiary"
                  onClick={handleDeletePost}
                  disabled={deletePost.isPending}
                  style={{ color: "var(--c-danger)" }}
                >
                  {deletePost.isPending ? "Deleting…" : "Delete"}
                </button>
              )}
            </div>

            {(postQuery.data.eventDate || postQuery.data.placeName) && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--s-2)" }}>
                {postQuery.data.eventDate && (
                  <span
                    className="badge"
                    data-pictured-date={postQuery.data.eventDate}
                    data-pictured-precision={postQuery.data.eventDatePrecision}
                    title={`Pictured: ${formatPicturedDate(postQuery.data.eventDate, postQuery.data.eventDatePrecision)}`}
                    style={{ fontSize: "0.7rem" }}
                  >
                    📅 Pictured · {formatPicturedDate(postQuery.data.eventDate, postQuery.data.eventDatePrecision)}
                  </span>
                )}
                {postQuery.data.placeName && (
                  <Link
                    to="/world"
                    className="badge"
                    style={{ fontSize: "0.7rem", textDecoration: "none" }}
                    title={postQuery.data.placeName}
                  >
                    📍 {postQuery.data.placeName}
                  </Link>
                )}
              </div>
            )}

            <p
              style={{
                whiteSpace: "pre-wrap",
                margin: 0,
                lineHeight: 1.6,
                fontSize: "1.05rem",
              }}
            >
              {postQuery.data.body}
            </p>

            {/* Single-image post: picture on left, like/comments on right
                (stacks on small screens). Multi-image: stacked layout. */}
            {postQuery.data.media.length === 1 && membership && slug ? (
              <div className="post-card__split">
                <div className="post-card__split-media">
                  <MediaTile
                    key={postQuery.data.media[0]!.id}
                    media={postQuery.data.media[0]!}
                    familySlug={membership.familySlug}
                    role={membership.role}
                    watermarkConfig={membership.watermark}
                    watermarkVars={watermarkVars}
                  />
                  <PostMediaStories
                    familySlug={membership.familySlug}
                    media={postQuery.data.media}
                    role={membership.role}
                    currentUserId={currentUserId}
                  />
                </div>
                <div className="post-card__split-aside">
                  <PostReactions familySlug={slug} post={postQuery.data} />
                  <PostComments
                    familySlug={slug}
                    postId={postQuery.data.id}
                    commentCount={postQuery.data.commentCount ?? 0}
                    currentUserId={currentUserId}
                    isAdmin={role ? can(role, "post:delete:any") : false}
                    defaultExpanded
                  />
                </div>
              </div>
            ) : (
              <>
                {postQuery.data.media.length > 0 && membership && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: "var(--s-2)",
                      marginTop: "var(--s-3)",
                    }}
                  >
                    {postQuery.data.media.map((m) => (
                      <MediaTile
                        key={m.id}
                        media={m}
                        familySlug={membership.familySlug}
                        role={membership.role}
                        watermarkConfig={membership.watermark}
                        watermarkVars={watermarkVars}
                      />
                    ))}
                  </div>
                )}
                {postQuery.data.media.length > 0 && membership && (
                  <div style={{ marginTop: "var(--s-3)" }}>
                    <PostMediaStories
                      familySlug={membership.familySlug}
                      media={postQuery.data.media}
                      role={membership.role}
                      currentUserId={currentUserId}
                    />
                  </div>
                )}

                {slug && (
                  <div
                    style={{
                      display: "flex",
                      gap: "var(--s-3)",
                      alignItems: "center",
                      marginTop: "var(--s-3)",
                      paddingTop: "var(--s-3)",
                      borderTop: "1px solid var(--c-border)",
                    }}
                  >
                    <PostReactions familySlug={slug} post={postQuery.data} />
                    <PostComments
                      familySlug={slug}
                      postId={postQuery.data.id}
                      commentCount={postQuery.data.commentCount ?? 0}
                      currentUserId={currentUserId}
                      isAdmin={role ? can(role, "post:delete:any") : false}
                    />
                  </div>
                )}
              </>
            )}
          </article>
        )}
      </main>
    </>
  );
}
